import { ref } from 'vue'
import { defineStore } from 'pinia'
import type {
  IAResult,
  IAResultDocument,
  SearchIAParams,
  FilterType,
  AdvanceSearchLikeParams,
  AdvanceSearchDislikeParams
} from '@/modules/documentSearcher/types/SearchIATypes'
import { TrackerEvents, trackEvent } from '@/plugins/tracker'
import {
  searchIA,
  likeAdvanceSearch as likeAdvanceSearchService,
  dislikeAdvanceSearch as dislikeAdvanceSearchService
} from '@/modules/documentSearcher/services/SearchIAService'
import { uuid } from '@/helpers/helpers'
// import { searchIAResponseMock } from './mock/storeMocks'

export type { SearchIAParams } from '@/modules/documentSearcher/types/SearchIATypes'

export const useSearchIAStore = defineStore('searchIA', () => {
  const searchIAResponse = ref<IAResult | null>(null)
  const isLoading = ref<boolean>(false)
  const queryDone = ref<boolean>(false)
  const hasError = ref<boolean>(false)
  const wasCanceled = ref<boolean>(false)
  const lastQuery = ref<string>('')
  const search_id = ref<string>('')
  let abortController: AbortController | null = null

  // Filter state
  const filterType = ref<FilterType | ''>('')
  const filterValue = ref<any>(null)
  const filterEntityName = ref<string>('')

  // Document to open (for communication between components)
  const documentToOpen = ref<IAResultDocument | null>(null)

  async function fetchSearchIA(params: SearchIAParams) {
    try {
      // Cancelar petición anterior si existe
      if (abortController) {
        abortController.abort()
      }

      //Mockking
      // const MOCK = true
      // if (true === MOCK) {
      //   console.log('Mocking search IA response')
      //   searchIAResponse.value = searchIAResponseMock.value
      //   isLoading.value = false
      //   queryDone.value = true
      //   abortController = null
      //   hasError.value = false
      //   wasCanceled.value = false
      //   return
      // }

      // Crear nuevo AbortController
      abortController = new AbortController()

      isLoading.value = true
      queryDone.value = true
      hasError.value = false
      wasCanceled.value = false
      lastQuery.value = params.search

      const { data } = await searchIA(params, 300000, abortController.signal)

      // Track the event for search results
      trackEvent(TrackerEvents.AI_SEARCH_RESULTS_VIEWED, {
        success_ia_results: data.retrieved_chunks?.length > 0,
        total_docs_ia_results: data.retrieved_documents?.length || 0,
        total_chunks_ia_results: data.retrieved_chunks?.length || 0,
        search_id: search_id.value
      })

      searchIAResponse.value = data || {}
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        console.log('Search request was canceled')
        wasCanceled.value = true
      } else {
        console.error('Error fetching advanced search:', error)
        hasError.value = true
        searchIAResponse.value = null
      }
    } finally {
      isLoading.value = false
      abortController = null
    }
  }

  const likeAdvanceSearch = (params: AdvanceSearchLikeParams) => likeAdvanceSearchService(params)

  const dislikeAdvanceSearch = (params: AdvanceSearchDislikeParams) => dislikeAdvanceSearchService(params)

  const cancelSearchIA = () => {
    if (abortController) {
      wasCanceled.value = true
      abortController.abort()
      abortController = null
      isLoading.value = false
    }
    queryDone.value = false
  }

  const resetSearchIAResponse = () => {
    searchIAResponse.value = null
    wasCanceled.value = false
  }

  const setFilter = (type: FilterType, value: any, entityName: string = '') => {
    filterType.value = type
    filterValue.value = value
    filterEntityName.value = entityName
  }

  const resetFilter = () => {
    filterType.value = ''
    filterValue.value = null
    filterEntityName.value = ''
  }

  const generateSearchId = () => {
    search_id.value = uuid()
  }

  const resetSearchId = () => {
    search_id.value = ''
  }

  const requestOpenDocument = (chunkId: string) => {
    if (!searchIAResponse.value?.retrieved_documents) {
      console.warn('No hay documentos disponibles')
      return
    }

    // Buscar el documento que contiene el chunk
    for (const document of searchIAResponse.value.retrieved_documents) {
      if (document.chunks && Array.isArray(document.chunks)) {
        const matchingChunk = document.chunks.find((chunk: any) => chunk.chunk_id === chunkId)
        if (matchingChunk) {
          documentToOpen.value = document
          return
        }
      }
    }
    console.warn('No se encontró documento para el chunk:', chunkId)
  }

  const clearDocumentToOpen = () => {
    documentToOpen.value = null
  }

  return {
    searchIAResponse,
    isLoading,
    hasError,
    queryDone,
    wasCanceled,
    lastQuery,
    search_id,
    filterType,
    filterValue,
    filterEntityName,
    documentToOpen,
    fetchSearchIA,
    likeAdvanceSearch,
    dislikeAdvanceSearch,
    cancelSearchIA,
    resetSearchIAResponse,
    setFilter,
    resetFilter,
    generateSearchId,
    resetSearchId,
    requestOpenDocument,
    clearDocumentToOpen
  }
})
